import { Box, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTrackQuery } from "@/modules/property-tracks/hooks/track.hook";
import WithErrorHandling from "@/modules/common/components/wrappers/with-error-handling";
import PropertyStage from "@/modules/property-tracks/components/desktop/property-stage";
import NextActions from "@/modules/property-tracks/components/next-actions";
import PropertyTrackTabs from "../tabs";
import TabContent from "./tab-content";
import LeadStatus from "@/modules/property-tracks/components/lead-status";
import { getJourneyByOldId } from "@/modules/lead-page/api/lead-page.v2.api";
import { useQuery } from "@tanstack/react-query";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import useLeadPageDataFetching from "@/modules/lead-page/hooks/lead-page-data-fetching.hook";
import SidePanel from "./side-panel";
import { usePropertyTrackTabs } from "@/modules/property-tracks/hooks/property-tracker-tab";
import { useEffect } from "react";
import { PROPERTY_TRACK_TAB_OPTIONS } from "@/modules/property-tracks/types";
import TrackTasks from "@/modules/property-tracks/components/task-management/track-tasks";
import { ReadonlyProvider } from "@/modules/common/context/read-only.context";
import BoxWithBackdrop from "@/modules/common/components/ui-kit/box-with-backdrop";

function PropertyTrackDesktop({ onError }: { onError?: () => void }) {
  const { trackId } = useParams<{ trackId: string }>();
  const {
    data: track,
    isError: isTrackError,
    isLoading: isTrackLoading,
  } = useTrackQuery({
    uid: trackId,
  });
  const { selectedTab, handleTabURLChange } = usePropertyTrackTabs({ track });

  const theme = useTheme();

  const { data: journeyOldIdResponse } = useQuery({
    queryKey: ["getJourneyByOldId", track?.journey?.uid],
    queryFn: () => getJourneyByOldId({ id: track?.journey?.uid || "" }),
    enabled: !!track?.journey?.uid,
    staleTime: Infinity,
  });

  const {
    leadData,
    isLoading: isLeadLoading,
    stagesData,
    isStagesLoading,
    leadIsError,
  } = useLeadPageDataFetching(journeyOldIdResponse?.data.nanoId, false);

  if (isTrackError || leadIsError) {
    onError?.();
  }

  const readonly: boolean = !!(track?.archivedAt ?? false);
  useEffect(() => {
    if (!selectedTab) {
      handleTabURLChange(PROPERTY_TRACK_TAB_OPTIONS[0]);
    }
  }, [selectedTab, handleTabURLChange]);

  return (
    <ReadonlyProvider value={{ readonly }}>
      <LeadPageContext.Provider
        value={{
          lead: leadData,
          stages: stagesData,
          isStagesLoading: isStagesLoading,
        }}
      >
        <BoxWithBackdrop
          disabled={readonly}
          allowInteractions={true}
          /* root: fixed viewport height, make sure it can be a flex container that contains constrained children */
          sx={{
            display: "flex",
            width: "100%",
            overflow: "hidden",
            height: "calc(100vh - 60px)",
          }}
        >
          <Box>
            <SidePanel isLoading={isLeadLoading} />
          </Box>

          {/* RIGHT COLUMN: must be a column flex so header + content split works */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column", // stack headers + content vertically
              minHeight: 0, // <-- allows children to shrink and enables inner scroll
              overflow: "hidden",
            }}
          >
            <Box sx={{ flex: "0 0 auto" }}>
              <PropertyStage track={track} onError={onError} />
            </Box>

            <Box sx={{ flex: "0 0 auto" }}>
              <LeadStatus isLoading={isLeadLoading} />
            </Box>

            <Box sx={{ flex: "0 0 auto" }}>
              <PropertyTrackTabs track={track} />
            </Box>

            <Box
              sx={{
                display: "flex",
                flex: 1,
                gap: 2,
                bgcolor: theme.nawy.colors.semantic.layer.secondary,
                padding: theme.nawy.spacing.md,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                }}
              >
                <TabContent
                  // selectedTab={selectedTab}
                  track={track}
                  isLoading={isTrackLoading}
                />
              </Box>
              <Box
                sx={{
                  flex: "0 0 auto",
                  alignSelf: "flex-start",
                  gap: (theme) => theme.nawy.spacing.md,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TrackTasks track={track} />
                <NextActions />
              </Box>
            </Box>
          </Box>
        </BoxWithBackdrop>
      </LeadPageContext.Provider>
    </ReadonlyProvider>
  );
}

const PropertyTrackDesktopWithError = WithErrorHandling(PropertyTrackDesktop);

export default PropertyTrackDesktopWithError;
